import config from '../config';

// 公共请求方法
const request = async (url, options = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  const token = config.getToken();
  if (token) {
    headers['X-Access-Token'] = token;
  }

  const fullURL = `${config.baseURL}${url}`;
  
  const response = await fetch(fullURL, {
    ...options,
    headers,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

// 获取股权穿透数据（替代原 sampleData）
// @param {string} [storeId='1847817655894917121'] - 店铺ID
// @param {string} authorizeCode 授权码
// @param targetMemberCode 表示展开到哪个节点后，就不在往下继续展开，后续通过异步接口查找返回
// @returns {Promise<{success: boolean, data?: any, error?: string}>}
export const fetchEquityData = async (storeId,authorizeCode,targetMemberCode) => {
  try {
    const result = await request(
      `/shop/app/common/clan/getClanMembersListByStoreId?authorizeCode=${authorizeCode}&storeId=${storeId}&targetMemberCode=${targetMemberCode}`,
      { method: 'GET' }
    );

    if (result.code === 200 || result.success) {
      return {
        success: true,
        data: result.data || result.result||result,
      };
    }
    return {
      success: false,
      error: result.msg || result.message || '数据加载失败',
    };
  } catch (error) {
    console.error('加载数据出错:', error);
    return {
      success: false,
      error: error.message || '数据加载失败',
    };
  }
};

// 根据节点ID获取子节点数据（替代原 asyncData）
// @param {string} pid - 父节点ID
// @returns {Promise<{success: boolean, data?: any, error?: string}>}
export const fetchCompanyChildren = async (pid) => {
  try {
    const result = await request(
      `/shop/app/common/clan/getChildrenByPid?pid=${pid}`,
      { method: 'GET' }
    );

    if (result.code === 200 || result.success) {
      return {
        success: true,
        data: result.data || result.result || result,
      };
    }
    return {
      success: false,
      error: result.msg || result.message || '子数据加载失败',
    };
  } catch (error) {
    console.error('加载子数据出错:', error);
    return {
      success: false,
      error: error.message || '子数据加载失败',
    };
  }
};

// 根据节点ID获取节点详情
// @param {string} id - 节点ID
// @returns {Promise<{success: boolean, data?: any, error?: string}>}
export const fetchClanMemberById = async (id) => {
  try {
    const result = await request(
      `/shop/app/common/clan/getClanMembersById?id=${id}`,
      { method: 'GET' }
    );

    if (result.code === 200 || result.success) {
      return {
        success: true,
        data: result.data || result.result|| result,
      };
    }
    return {
      success: false,
      error: result.msg || result.message || '获取节点详情失败',
    };
  } catch (error) {
    console.error('获取节点详情出错:', error);
    return {
      success: false,
      error: error.message || '获取节点详情失败',
    };
  }
};

/**
 * 唤起手机地图进行导航
 * @param {number} lat - 目标纬度 (如 39.904989)
 * @param {number} lng - 目标经度 (如 116.405285)
 * @param {string} [name] - 可选，目的地名称，用于显示
 * @returns {boolean} 是否成功发起跳转
 */
export const openMapNavigation = (lat, lng, name = '目的地') => {
    // 1. 坐标有效性检查
    if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
        alert('无法获取有效的位置信息，请稍后重试。');
        return false;
    }

    // 对名称进行编码，确保URL参数安全
    const encodedName = encodeURIComponent(name);
    // 注意：大部分地图App的经纬度参数顺序是 纬度,经度
    const coordString = `${lat},${lng}`;

    // 2. 获取User-Agent，用于简单区分平台
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    
    // 3. 地图跳转链接库（按优先级排序）
    const mapSchemes = [
        // 高德地图 - 国内用户量大
        {
            name: '高德地图',
            scheme: isIOS ? 'iosamap://' : 'androidamap://',
            // path: 'path=导航路径规划的参数' 
            // 导航需要使用完整的 route plan 链接
            downloadUrl: 'https://uri.amap.com/marker?position=' + coordString,
            getUrl: () => `https://uri.amap.com/marker?position=${coordString}&name=${encodedName}`
        },
        // 百度地图 - 国内用户量大
        {
            name: '百度地图',
            scheme: 'baidumap://',
            downloadUrl: `https://api.map.baidu.com/marker?location=${coordString}&title=${encodedName}&content=${encodedName}&output=html`,
            getUrl: () => `baidumap://map/direction?destination=${coordString}&mode=driving&coord_type=gcj02`
        },
        // 腾讯地图 - 使用场景丰富
        {
            name: '腾讯地图',
            scheme: 'qqmap://',
            downloadUrl: `https://apis.map.qq.com/uri/v1/marker?marker=coord:${coordString};title:${encodedName}`,
            getUrl: () => `qqmap://map/routeplan?type=drive&from=我的位置&to=${encodedName}&tocoord=${coordString}`
        },
        // Apple Maps (仅 iOS)
        ...(isIOS ? [{
            name: 'Apple Maps',
            scheme: 'maps://',
            getUrl: () => `maps://?q=${coordString}`,
            // iOS降级链接是 maps.apple.com
            downloadUrl: `https://maps.apple.com/?q=${coordString}`
        }] : [])
    ];

    // 4. 尝试唤起地图
    for (let map of mapSchemes) {
        // 如果用户没装该地图，继续尝试下一个
        // 注意：在iOS上，scheme检查需要白名单配置，这里简单尝试打开，如果失败就继续
        try {
            const url = map.getUrl();
            // 方法1：通过创建隐藏的 iframe 尝试打开，如果失败，不会影响页面跳转
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = url;
            document.body.appendChild(iframe);
            
            // 设置一个定时器，如果页面没有跳转，说明App未安装，移除iframe并尝试下一个
            setTimeout(() => {
                document.body.removeChild(iframe);
                // 如果当前是最后一个地图，且都没成功，再执行降级方案
                if (map === mapSchemes[mapSchemes.length - 1]) {
                    // 5. 终极降级：打开一个高德地图的通用网页（国内稳定）
                    window.location.href = `https://uri.amap.com/marker?position=${coordString}&name=${encodedName}`;
                }
            }, 500);
            
            // 方法2：同时直接尝试跳转（部分浏览器支持更直接）
            // 这里采用定时器方案，避免因App未安装导致页面卡住
            return true; // 认为已尝试跳转，不再继续执行
        } catch (error) {
            console.log(`打开 ${map.name} 失败`, error);
            // 继续尝试下一个
        }
    }

    // 如果所有scheme都无法打开，最后降级到高德地图的H5页面
    window.location.href = `https://uri.amap.com/marker?position=${coordString}&name=${encodedName}`;
    return true;
};

// 在你的点击事件中使用它
// onClick={() => openMapNavigation(detailData.cemeteryLocationLatitude, detailData.cemeteryLocationLongitude, '墓地名称')}

